import React, { useEffect, useState } from "react";
import { Box } from "@mui/joy";
import FullPageContainer from "pages/container/FullPageContainer";
import { theme } from "styles/theme";
import useTaskEditorStore from "hooks/useTaskEditorStore";
import {
  cloneTaskEditorState,
  createEmptyTaskEditorState,
} from "./utils/taskEditorDiff";
import type { TaskEditorState } from "./utils/taskEditorUtils";
import AgentChatPanel from "./AgentChatPanel";
import TaskListPanel from "./TaskListPanel";
import TaskMetadataModal, { TaskMetadataValues } from "./TaskMetadataModal";
import TaskWorkspacePanel from "./TaskWorkspacePanel";

type MetaModalState =
  | { mode: "create" }
  | {
      mode: "edit";
      initialValues: TaskMetadataValues;
      originalTaskId: string;
    };

const TASK_LIST_WIDTH = 160;
const RESIZE_HANDLE_WIDTH = 8;
const CHAT_MIN_WIDTH = 280;
const WORKSPACE_WIDTH_MIN = 420;
const WORKSPACE_WIDTH_MAX = 1600;
const WORKSPACE_WIDTH_DEFAULT = 480;
const WORKSPACE_WIDTH_STORAGE_KEY = "taskEditorWorkspaceWidth";

function loadInitialWorkspaceWidth(): number {
  if (typeof window === "undefined") {
    return WORKSPACE_WIDTH_DEFAULT;
  }
  try {
    const saved = window.localStorage.getItem(WORKSPACE_WIDTH_STORAGE_KEY);
    if (!saved) return WORKSPACE_WIDTH_DEFAULT;
    const parsed = parseInt(saved, 10);
    if (!Number.isFinite(parsed)) return WORKSPACE_WIDTH_DEFAULT;
    return Math.min(Math.max(parsed, WORKSPACE_WIDTH_MIN), WORKSPACE_WIDTH_MAX);
  } catch {
    return WORKSPACE_WIDTH_DEFAULT;
  }
}

export default function TaskEditorPage() {
  const {
    publishedTasks,
    taskDraftsByTaskId,
    publishTask,
    saveTaskDraft,
    removeTaskDraft,
  } = useTaskEditorStore();

  const [activeTaskId, setActiveTaskId] = useState<string | undefined>(
    undefined
  );
  const [currentEditorState, setCurrentEditorState] = useState<
    TaskEditorState | undefined
  >(undefined);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [metaModal, setMetaModal] = useState<MetaModalState | null>(null);
  const [isSavingMeta, setIsSavingMeta] = useState(false);
  const [isSavingContent, setIsSavingContent] = useState(false);
  const [workspaceWidth, setWorkspaceWidth] = useState<number>(
    loadInitialWorkspaceWidth
  );
  const [isResizingWorkspace, setIsResizingWorkspace] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined") return;
    try {
      window.localStorage.setItem(
        WORKSPACE_WIDTH_STORAGE_KEY,
        String(workspaceWidth)
      );
    } catch {
      // ignore
    }
  }, [workspaceWidth]);

  const handleWorkspaceResizeStart = (event: React.MouseEvent) => {
    event.preventDefault();
    const startX = event.clientX;
    const startWidth = workspaceWidth;
    const dynamicMax = Math.min(
      WORKSPACE_WIDTH_MAX,
      Math.max(
        WORKSPACE_WIDTH_MIN,
        window.innerWidth - TASK_LIST_WIDTH - CHAT_MIN_WIDTH
      )
    );
    setIsResizingWorkspace(true);

    const handleMove = (moveEvent: MouseEvent) => {
      const delta = startX - moveEvent.clientX;
      const next = Math.min(
        Math.max(startWidth + delta, WORKSPACE_WIDTH_MIN),
        dynamicMax
      );
      setWorkspaceWidth(next);
    };

    const handleEnd = () => {
      document.removeEventListener("mousemove", handleMove);
      document.removeEventListener("mouseup", handleEnd);
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
      setIsResizingWorkspace(false);
    };

    document.addEventListener("mousemove", handleMove);
    document.addEventListener("mouseup", handleEnd);
    document.body.style.cursor = "col-resize";
    document.body.style.userSelect = "none";
  };

  useEffect(() => {
    if (!activeTaskId && publishedTasks.length > 0) {
      setActiveTaskId(publishedTasks[0].taskId);
    }
  }, [activeTaskId, publishedTasks]);

  useEffect(() => {
    if (!activeTaskId) {
      setCurrentEditorState(undefined);
      setHasUnsavedChanges(false);
      return;
    }
    const draft = taskDraftsByTaskId[activeTaskId];
    const baseTask = publishedTasks.find(
      (entry) => entry.taskId === activeTaskId
    );
    if (draft) {
      setCurrentEditorState(cloneTaskEditorState(draft));
      setHasUnsavedChanges(true);
    } else if (baseTask) {
      setCurrentEditorState(cloneTaskEditorState(baseTask));
      setHasUnsavedChanges(false);
    } else {
      setCurrentEditorState(undefined);
      setHasUnsavedChanges(false);
    }
    // publishedTasks/taskDraftsByTaskId 변화는 의도적으로 의존성에서 제외.
    // 사용자 편집 중 외부 atom 갱신이 currentEditorState 를 덮어쓰지 않도록.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTaskId]);

  const handleCreateTask = () => {
    setMetaModal({ mode: "create" });
  };

  const handleEditMeta = (taskId: string) => {
    const task = publishedTasks.find((entry) => entry.taskId === taskId);
    if (!task) {
      return;
    }
    setMetaModal({
      mode: "edit",
      originalTaskId: task.taskId,
      initialValues: {
        taskId: task.taskId,
        title: task.title,
        setNumber: task.setNumber,
        isKeyActivity: task.isKeyActivity ?? false,
      },
    });
  };

  const handleMetaSubmit = async (values: TaskMetadataValues) => {
    if (!metaModal || isSavingMeta) {
      return;
    }

    let baseState: TaskEditorState;
    let currentTaskId: string | undefined;

    if (metaModal.mode === "edit") {
      // 편집 중인 local state 가 있으면 그것을 우선 사용 (변경된 블록 보존)
      const localMatch =
        currentEditorState &&
        currentEditorState.taskId === metaModal.originalTaskId
          ? currentEditorState
          : undefined;
      const fallbackPublished = publishedTasks.find(
        (entry) => entry.taskId === metaModal.originalTaskId
      );
      const source = localMatch ?? fallbackPublished;
      baseState = source
        ? cloneTaskEditorState(source as TaskEditorState)
        : createEmptyTaskEditorState();
      currentTaskId = metaModal.originalTaskId;
    } else {
      baseState = createEmptyTaskEditorState();
      currentTaskId = undefined;
    }

    const nextState: TaskEditorState = {
      ...baseState,
      taskId: values.taskId,
      title: values.title,
      setNumber: values.setNumber,
      isKeyActivity: values.isKeyActivity,
    };

    setIsSavingMeta(true);
    try {
      const saved = await publishTask(nextState, currentTaskId);
      if (currentTaskId) {
        await removeTaskDraft(currentTaskId);
      }
      setActiveTaskId(saved.taskId);
      setCurrentEditorState(cloneTaskEditorState(saved));
      setHasUnsavedChanges(false);
      setMetaModal(null);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error("[TaskEditorV2] failed to save metadata", error);
    } finally {
      setIsSavingMeta(false);
    }
  };

  const handleChangeEditorState = (next: TaskEditorState) => {
    setCurrentEditorState(next);
    setHasUnsavedChanges(true);
    saveTaskDraft(next.taskId, next);
  };

  const handleSave = async () => {
    if (!currentEditorState || isSavingContent) {
      return;
    }
    setIsSavingContent(true);
    try {
      const saved = await publishTask(
        currentEditorState,
        currentEditorState.taskId
      );
      await removeTaskDraft(currentEditorState.taskId);
      setCurrentEditorState(cloneTaskEditorState(saved));
      setHasUnsavedChanges(false);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error("[TaskEditorV2] failed to save content", error);
    } finally {
      setIsSavingContent(false);
    }
  };

  const existingTaskIds = publishedTasks.map((entry) => entry.taskId);

  return (
    <FullPageContainer>
      <Box
        sx={{
          display: "grid",
          position: "relative",
          gridTemplateColumns: `${TASK_LIST_WIDTH}px minmax(0, 1fr) ${workspaceWidth}px`,
          height: "100%",
          width: "100%",
          minHeight: 0,
          overflow: "hidden",
          backgroundColor: theme.vars.palette.Background.Level1,
        }}
      >
        <TaskListPanel
          tasks={publishedTasks}
          activeTaskId={activeTaskId}
          onSelectTask={setActiveTaskId}
          onCreateTask={handleCreateTask}
          onEditMeta={handleEditMeta}
        />

        <AgentChatPanel
          taskId={activeTaskId}
          currentTask={currentEditorState}
          onTaskPatch={handleChangeEditorState}
        />

        <Box
          onMouseDown={handleWorkspaceResizeStart}
          role="separator"
          aria-orientation="vertical"
          aria-label="우측 패널 크기 조정"
          sx={{
            position: "absolute",
            top: 0,
            bottom: 0,
            right: `${workspaceWidth}px`,
            width: `${RESIZE_HANDLE_WIDTH}px`,
            cursor: "col-resize",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            backgroundColor: "transparent",
            zIndex: 2,
            "&:hover .resize-grip": {
              backgroundColor: theme.vars.palette.Content.Medium,
              opacity: 1,
            },
          }}
        >
          <Box
            className="resize-grip"
            sx={{
              width: "3px",
              height: "64px",
              borderRadius: "2px",
              backgroundColor: isResizingWorkspace
                ? theme.vars.palette.Content.Medium
                : theme.vars.palette.Content.Weak,
              opacity: isResizingWorkspace ? 1 : 0,
              transition: isResizingWorkspace
                ? "none"
                : "opacity 0.15s, background-color 0.15s",
              pointerEvents: "none",
            }}
          />
        </Box>

        <TaskWorkspacePanel
          editorState={currentEditorState}
          onChangeEditorState={handleChangeEditorState}
          hasUnsavedChanges={hasUnsavedChanges}
          isSaving={isSavingContent}
          onSave={handleSave}
          onEditMeta={() => {
            if (activeTaskId) {
              handleEditMeta(activeTaskId);
            }
          }}
        />
      </Box>

      <TaskMetadataModal
        open={metaModal !== null}
        mode={metaModal?.mode ?? "create"}
        initialValues={
          metaModal?.mode === "edit" ? metaModal.initialValues : undefined
        }
        existingTaskIds={existingTaskIds}
        excludeTaskId={
          metaModal?.mode === "edit" ? metaModal.originalTaskId : undefined
        }
        onClose={() => {
          if (!isSavingMeta) {
            setMetaModal(null);
          }
        }}
        onSubmit={handleMetaSubmit}
      />
    </FullPageContainer>
  );
}
